/* Setup tab — all exchange + Telegram + Risk credential cards.
   Extracted from Dashboard.jsx (which was >3300 lines) so each card is easy
   to find and edit. Blofin uses the generic ExchangeCredsCard since it takes
   the standard {api_key, api_secret, passphrase} shape. */
import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  observability, settings, telegram, exchanges, channels,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge, StatusPill, cn } from "@/components/atoms";
import {
  TelegramLogo, ChartLineUp, ShieldCheck, PlugsConnected, ArrowsClockwise,
  Broadcast,
} from "@phosphor-icons/react";
import { Field, ToggleRow, SliderRow } from "@/components/dashboard/formfields";

/* ============ SETUP CONTAINER ============ */
export default function SetupSection() {
  const ovQ = useQuery({ queryKey: ["overview"], queryFn: async () => (await observability.overview()).data });
  const showMulti = ovQ.data?.multi_channel_enabled;
  return (
    <div className="space-y-4" data-testid="section-setup">
      {showMulti && (
        <div className="rounded-md border border-slate-200 bg-slate-50/60 p-4">
          <p className="overline text-slate-700">Multi-exchange setup</p>
          <p className="text-sm text-slate-600 mt-1">
            Configure each exchange independently. Risk profile and kill switches now live per
            <strong> Channel </strong>
            (under the Channels tab) — a channel routes one Telegram source to one exchange with its own risk envelope.
          </p>
        </div>
      )}
      {showMulti && <ConnectionSummaryStrip ov={ovQ.data} />}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
        <TelegramCard />
        <WeexCard />
        {showMulti && <ExchangeCredsCard exchange="bitget" title="Bitget" />}
        {showMulti && <ExchangeCredsCard exchange="blofin" title="Blofin" hint={<BlofinSetupHint />} />}
        {showMulti && <ExchangeCredsCard exchange="gate" title="Gate" hint={<GateSetupHint />} />}
        {!showMulti && <RiskCard />}
      </div>
      {showMulti && <ChannelRoutingCard ov={ovQ.data} />}
    </div>
  );
}

/* Real connection status roll-up — counts derived from the same by_exchange
   block the header badges and Overview already read, just summarized. */
function ConnectionSummaryStrip({ ov }) {
  const chQ = useQuery({ queryKey: ["channels"], queryFn: async () => (await channels.list()).data });
  const byEx = ov?.by_exchange || {};
  const exEntries = Object.entries(byEx);
  const connectedCount = exEntries.filter(([, s]) => s.status === "connected" || s.status === "ok").length;
  const paperCount = exEntries.filter(([, s]) => s.paper_mode).length;
  const liveCount = exEntries.length - paperCount;
  const chList = chQ.data || [];
  const routed = chList.filter((c) => byEx[c.exchange]).length;

  return (
    <div className="card-surface p-4 flex items-center gap-6 flex-wrap" data-testid="connection-summary-strip">
      <p className="overline shrink-0">Connection status</p>
      <span className="flex items-center gap-1.5 text-sm">
        <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
        <span className="font-bold">{connectedCount} of {exEntries.length}</span>
        <span className="text-slate-500">connected</span>
      </span>
      <span className="text-sm text-slate-500">
        <span className="font-bold text-slate-900">{paperCount}</span> in paper mode
      </span>
      <span className="text-sm text-slate-500">
        <span className="font-bold text-slate-900">{liveCount}</span> in live mode
      </span>
      <span className="text-sm text-slate-500">
        <span className="font-bold text-slate-900">{routed}</span> of {chList.length} channels routed
        {chList.length - routed > 0 ? ` · ${chList.length - routed} unassigned` : ""}
      </span>
    </div>
  );
}

/* Real per-channel routing table — which exchange account each channel
   places orders on, and whether that exchange is currently paper or live.
   Margin mode always reads "Cross" today: per-channel isolated margin was
   built but never wired to real order placement (open_type stays hardcoded
   "cross") and was dropped rather than finished — see
   [[isolated_margin_not_wired_to_real_orders]]. */
function ChannelRoutingCard({ ov }) {
  const chQ = useQuery({ queryKey: ["channels"], queryFn: async () => (await channels.list()).data });
  const byEx = ov?.by_exchange || {};
  const list = chQ.data || [];
  if (list.length === 0) return null;

  return (
    <div className="card-surface p-6" data-testid="channel-routing-card">
      <p className="overline">Signal routing</p>
      <h3 className="font-display text-xl mt-1">Channel Routing</h3>
      <p className="text-sm text-slate-500 mt-1 mb-4">
        Which exchange account each signal channel places real orders on.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
            <tr>
              <th className="text-left py-2 px-2">Channel</th>
              <th className="text-left py-2 px-2">Exchange</th>
              <th className="text-left py-2 px-2">Mode</th>
              <th className="text-left py-2 px-2">Margin mode</th>
              <th className="text-left py-2 px-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {list.map((c) => {
              const exStatus = byEx[c.exchange];
              const paper = !!exStatus?.paper_mode;
              const idle = exStatus?.balance_usdt === 0 && !paper;
              return (
                <tr key={c.id} className="border-b border-slate-100" data-testid={`routing-row-${c.id}`}>
                  <td className="py-2.5 px-2 font-medium">{c.name}</td>
                  <td className="py-2.5 px-2 uppercase text-slate-600">{c.exchange}</td>
                  <td className="py-2.5 px-2"><Badge variant={paper ? "warn" : "profit"}>{paper ? "Paper" : "Live"}</Badge></td>
                  <td className="py-2.5 px-2 text-slate-600 capitalize">{c.risk_profile?.margin_mode || "cross"}</td>
                  <td className="py-2.5 px-2">
                    {!c.enabled ? <Badge variant="muted">Paused</Badge>
                      : idle ? <Badge variant="muted">Idle · $0 balance</Badge>
                      : <Badge variant="profit">Active</Badge>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-4 p-4 rounded-md bg-[#EEF0FF] flex items-start gap-3">
        <span className="text-[#4F46E5] text-lg leading-none">ⓘ</span>
        <div>
          <p className="text-sm font-bold text-[#4F46E5]">No cross-exchange netting</p>
          <p className="text-xs text-slate-600 mt-1 leading-relaxed">
            Each channel is pinned to exactly one exchange account. Channels sharing the same
            exchange share its real balance and margin — position sizing for each is isolated
            only where a virtual per-channel balance is explicitly enabled, not by the exchange itself.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ============ BLOFIN SETUP HINT ============ */
function BlofinSetupHint() {
  return (
    <div className="mt-3 p-3 rounded-md bg-sky-50 border border-sky-200 text-xs text-slate-700 leading-relaxed" data-testid="blofin-setup-hint">
      <p className="font-semibold text-sky-900 mb-1">🔑 Creating your Blofin API key</p>
      <ol className="list-decimal list-inside space-y-1">
        <li>Sign in at <a className="text-sky-700 underline" href="https://blofin.com/account/apis" target="_blank" rel="noreferrer">blofin.com/account/apis</a>.</li>
        <li>Choose <strong>API Type: BloFin MCP</strong> (or standard) and enable <strong>Trade</strong> permission.</li>
        <li>Copy the API Key, Secret, and Passphrase into the fields above.</li>
      </ol>
      <div className="mt-2 p-2 rounded bg-amber-50 border border-amber-300 text-amber-900">
        <p className="font-semibold mb-0.5">⚠️ Paper-mode + live key = &ldquo;Access key does not exist&rdquo;</p>
        <p>Blofin&apos;s demo and live environments have <strong>separate</strong> API keys.
          If you get this error, either <strong>turn Paper mode OFF</strong> to use your live key, OR create a
          dedicated demo key from Blofin&apos;s demo trading site (
          <a className="text-amber-800 underline" href="https://demo-trading.blofin.com" target="_blank" rel="noreferrer">demo-trading.blofin.com</a>
          ) and paste that here while keeping Paper mode ON.
        </p>
      </div>
    </div>
  );
}

/* ============ GATE SETUP HINT ============ */
const GATE_TESTNET_URL = "https://api-testnet.gateapi.io";

function GateSetupHint() {
  return (
    <div className="mt-3 p-3 rounded-md bg-sky-50 border border-sky-200 text-xs text-slate-700 leading-relaxed" data-testid="gate-setup-hint">
      <p className="font-semibold text-sky-900 mb-1">🔑 Creating your Gate API key</p>
      <ol className="list-decimal pl-4 space-y-1">
        <li>Gate &rarr; API Management &rarr; create a key with <strong>Perpetual Futures: read + write</strong> only (no withdrawals).</li>
        <li>Gate has <strong>no passphrase</strong> — just the key and secret.</li>
        <li>Account must be in <strong>one-way (single) position mode</strong>.</li>
        <li>Testnet keys come from Gate&apos;s testnet site and only work with Environment = Testnet below (turn Paper mode OFF to use them).</li>
      </ol>
    </div>
  );
}

/* ============ EXCHANGE CREDS CARD (Bitget / Blofin / Gate) ============ */
function ExchangeCredsCard({ exchange, title, hint = null }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["exchange", exchange],
    queryFn: async () => (await exchanges.get(exchange)).data,
  });
  const hasPassphrase = exchange !== "gate";
  const [form, setForm] = useState({ api_key: "", api_secret: "", passphrase: "", paper_mode: true, base_url: "" });
  const [dirty, setDirty] = useState(false);
  const [probeResult, setProbeResult] = useState(null);

  useEffect(() => {
    if (q.data) setForm({
      api_key: q.data.api_key || "",
      api_secret: "",
      passphrase: "",
      paper_mode: q.data.paper_mode ?? true,
      base_url: q.data.base_url || "",
    });
  }, [q.data]);

  const save = useMutation({
    mutationFn: () => exchanges.put(exchange, {
      api_key: form.api_key || null,
      api_secret: form.api_secret || null,
      passphrase: form.passphrase || null,
      paper_mode: form.paper_mode,
      // Gate only: empty = live endpoint, the testnet URL = Gate testnet.
      ...(exchange === "gate" ? { base_url: form.base_url || "https://api.gateio.ws" } : {}),
    }),
    onSuccess: () => {
      toast.success(`${title} settings saved`);
      setDirty(false);
      qc.invalidateQueries({ queryKey: ["exchange", exchange] });
      qc.invalidateQueries({ queryKey: ["overview"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const testConn = useMutation({
    mutationFn: () => exchanges.test(exchange),
    onSuccess: (r) => {
      if (r.data.ok) toast.success(`${title} connection OK`);
      else toast.error(`${title} test failed: ${JSON.stringify(r.data).slice(0, 200)}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Test failed"),
  });

  // Per-exchange live-probe notional. Blofin's BTC-USDT minimum lot is
  // 0.1 contracts (≈ $6.50), so $10 keeps the probe above the floor. Other
  // exchanges tolerate $5 which is friendlier to test balances.
  const probeNotional = exchange === "blofin" ? 10 : 5;

  const probe = useMutation({
    mutationFn: (live) => exchanges.debugOrder(exchange, {
      symbol: "BTC/USDT", side: "long", notional_usdt: probeNotional,
      i_understand_this_places_real_orders: live,
    }),
    onSuccess: (r) => setProbeResult(r.data),
    onError: (e) => toast.error(e?.response?.data?.detail || "Probe failed"),
  });

  const [balanceResult, setBalanceResult] = useState(null);
  const debugBalance = useMutation({
    mutationFn: () => exchanges.debugBalance(exchange),
    onSuccess: (r) => setBalanceResult(r.data),
    onError: (e) => toast.error(e?.response?.data?.detail || "Balance debug failed"),
  });

  const closeAll = useMutation({
    mutationFn: () => exchanges.closeAllPositions(exchange),
    onSuccess: (r) => {
      setProbeResult(r.data);
      const c = r.data?.closed_count ?? 0;
      const t = r.data?.total_positions ?? 0;
      if (t === 0) toast.info(`${title}: no open positions to close.`);
      else if (c === t) toast.success(`${title}: closed all ${c} position(s).`);
      else toast.warning(`${title}: closed ${c} of ${t} position(s). Check details.`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Close-all failed"),
  });

  const toggleAuto = useMutation({
    mutationFn: (enabled) => exchanges.setAutoTrading(exchange, enabled),
    onSuccess: (r) => {
      toast[r.data.auto_trading ? "success" : "info"](
        r.data.auto_trading ? `${title} auto-trading ON` : `${title} auto-trading OFF`
      );
      qc.invalidateQueries({ queryKey: ["exchange", exchange] });
      qc.invalidateQueries({ queryKey: ["overview"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Toggle failed"),
  });

  const setField = (k, v) => { setForm({ ...form, [k]: v }); setDirty(true); };
  const autoOn = !!q.data?.auto_trading;

  return (
    <div className="card-surface p-6" data-testid={`${exchange}-card`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="overline">{title} API</p>
          <h3 className="font-display text-xl mt-1">{title}</h3>
        </div>
        <StatusPill
          status={q.data?.paper_mode ? "paper" :
                  q.data?.status === "connected" ? "connected" : "error"}
          label={q.data?.paper_mode ? "PAPER MODE" : (q.data?.status || "not_configured").toUpperCase()}
        />
      </div>

      <div className={cn(
             "flex items-center justify-between p-4 rounded-md border-2 mb-4 transition-colors",
             autoOn
               ? "bg-emerald-50 border-emerald-300"
               : "bg-red-50 border-red-300"
           )}
           data-testid={`${exchange}-auto-trading-row`}>
        <div className="flex items-center gap-3">
          <div className={cn(
                 "w-3 h-3 rounded-full",
                 autoOn ? "bg-emerald-500 animate-pulse" : "bg-red-500"
               )} />
          <div>
            <p className="text-sm font-semibold">
              {title} · {autoOn ? "ACTIVE" : "INACTIVE"}
            </p>
            <p className="text-xs text-slate-600">
              {autoOn
                ? "Auto-executing signals routed to this exchange."
                : "Signals parsed but NOT executed for this exchange."}
            </p>
          </div>
        </div>
        <Button
          data-testid={`${exchange}-auto-trading-switch`}
          onClick={() => toggleAuto.mutate(!autoOn)}
          disabled={toggleAuto.isPending}
          size="sm"
          className={cn(
            "min-w-[110px] font-medium",
            autoOn
              ? "bg-red-600 hover:bg-red-700 text-white"
              : "bg-emerald-600 hover:bg-emerald-700 text-white"
          )}
        >
          {toggleAuto.isPending
            ? "…"
            : autoOn ? "Deactivate" : "Activate"}
        </Button>
      </div>
      <div className="space-y-3">
        <Field label="API Key" value={form.api_key} onChange={(v) => setField("api_key", v)}
               testId={`${exchange}-api-key`} placeholder="Paste your API key" />
        <Field label="API Secret" value={form.api_secret} onChange={(v) => setField("api_secret", v)}
               testId={`${exchange}-api-secret`} placeholder="•••••• (blank = keep current)" type="password" />
        {hasPassphrase && (
          <Field label="Passphrase" value={form.passphrase} onChange={(v) => setField("passphrase", v)}
                 testId={`${exchange}-passphrase`} placeholder="•••••• (blank = keep current)" type="password" />
        )}
        {exchange === "gate" && (
          <div className="flex items-center justify-between p-3 rounded-md bg-slate-50 border border-slate-200">
            <div>
              <p className="text-sm font-medium">Environment</p>
              <p className="text-xs text-slate-500">
                {form.base_url === GATE_TESTNET_URL ? "Testnet (fake money, 63 contracts)" : "Live trading"}
              </p>
            </div>
            <Switch checked={form.base_url === GATE_TESTNET_URL}
                    onCheckedChange={(v) => setField("base_url", v ? GATE_TESTNET_URL : "https://api.gateio.ws")}
                    data-testid="gate-testnet-toggle" />
          </div>
        )}
        <div className="flex items-center justify-between p-3 rounded-md bg-slate-50 border border-slate-200">
          <div>
            <p className="text-sm font-medium">Paper mode</p>
            <p className="text-xs text-slate-500">Simulated fills; no capital at risk.</p>
          </div>
          <Switch checked={form.paper_mode} onCheckedChange={(v) => setField("paper_mode", v)}
                  data-testid={`${exchange}-paper-toggle`} />
        </div>
      </div>
      {dirty && (
        <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          Unsaved changes — click Save to persist.
        </div>
      )}
      <div className="mt-4">
        <p className="overline mb-2">Debug tools</p>
        <div className="flex flex-wrap gap-2">
          <Button data-testid={`${exchange}-save`} onClick={() => save.mutate()}
                  disabled={save.isPending || !dirty}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
          <Button data-testid={`${exchange}-test`} onClick={() => testConn.mutate()}
                  variant="outline" size="sm" disabled={testConn.isPending}>
            {testConn.isPending ? "Testing…" : "🔌 Test connection"}
          </Button>
          <Button data-testid={`${exchange}-probe-dry`}
                  onClick={() => probe.mutate(false)}
                  variant="outline" size="sm" disabled={probe.isPending}>
            🔬 Dry-run probe
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid={`${exchange}-probe-live`}
                      onClick={(e) => {
                        if (q.data?.paper_mode) {
                          e.preventDefault();
                          toast.error(`Turn off paper mode first — live probe places a real $${probeNotional} order.`);
                        }
                      }}
                      variant="outline" size="sm" disabled={probe.isPending}
                      className="border-red-300 text-red-700 hover:bg-red-50">
                ⚠ Live probe (${probeNotional})
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid={`${exchange}-probe-live-dialog`}>
              <AlertDialogHeader>
                <AlertDialogTitle>Place a REAL ${probeNotional} order on {title}?</AlertDialogTitle>
                <AlertDialogDescription>
                  A live market BTC/USDT long will be opened using ~${probeNotional} of margin.
                  Close it immediately on {title}&apos;s app after this smoke test — the
                  position is <strong>not</strong> monitored by our engine.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid={`${exchange}-probe-live-cancel`}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  data-testid={`${exchange}-probe-live-confirm`}
                  onClick={() => probe.mutate(true)}
                  className="bg-red-600 hover:bg-red-700">
                  Place live ${probeNotional} order
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button data-testid={`${exchange}-fetch-balance`}
                  onClick={() => debugBalance.mutate()}
                  variant="outline" size="sm" disabled={debugBalance.isPending}>
            💰 Fetch balance JSON
          </Button>
          <Button data-testid={`${exchange}-debug-balance`}
                  onClick={() => debugBalance.mutate()}
                  variant="outline" size="sm" disabled={debugBalance.isPending}
                  className="border-amber-300 text-amber-700 hover:bg-amber-50">
            🔍 Debug balance JSON
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid={`${exchange}-close-all`}
                      variant="outline" size="sm" disabled={closeAll.isPending}
                      className="border-orange-400 text-orange-700 hover:bg-orange-50">
                {closeAll.isPending ? "Closing…" : "🛑 Close all positions"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid={`${exchange}-close-all-dialog`}>
              <AlertDialogHeader>
                <AlertDialogTitle>Market-close ALL open positions on {title}?</AlertDialogTitle>
                <AlertDialogDescription>
                  Every open position on {title} will be closed with reduce-only market orders.
                  Use this to unwind smoke-test / probe orders in one click. Does not affect
                  trades on other exchanges.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid={`${exchange}-close-all-cancel`}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  data-testid={`${exchange}-close-all-confirm`}
                  onClick={() => closeAll.mutate()}
                  className="bg-orange-600 hover:bg-orange-700">
                  Close everything
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
      {balanceResult && (
        <div className="mt-4 text-[11px] mono bg-slate-900 text-amber-200 p-3 rounded-md overflow-x-auto max-h-80"
             data-testid={`${exchange}-balance-result`}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-slate-400">Balance debug · {exchange}</span>
            <button
              className="text-indigo-300 hover:text-indigo-100 underline text-[10px]"
              onClick={() => { navigator.clipboard.writeText(JSON.stringify(balanceResult, null, 2)); toast.success("Copied"); }}
              data-testid={`${exchange}-balance-copy`}
            >Copy JSON</button>
          </div>
          <pre>{JSON.stringify(balanceResult, null, 2)}</pre>
        </div>
      )}
      {probeResult && (
        <div className="mt-4 text-[11px] mono bg-slate-900 text-emerald-300 p-3 rounded-md overflow-x-auto max-h-80"
             data-testid={`${exchange}-probe-result`}>
          <pre>{JSON.stringify(probeResult, null, 2)}</pre>
        </div>
      )}
      {hint}
    </div>
  );
}

/* ============ TELEGRAM CARD ============ */
function TelegramCard() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["tg"], queryFn: async () => (await settings.getTelegram()).data });
  const [form, setForm] = useState({ api_id: "", api_hash: "", phone: "", alerts_channel: "" });
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [reconnectRequired, setReconnectRequired] = useState(false);

  const extractError = (e, fallback) => {
    const d = e?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (d && typeof d === "object" && d.message) return d.message;
    return e?.message || fallback;
  };
  const isReconnectRequired = (e) => {
    return e?.response?.status === 401 &&
      e?.response?.data?.detail?.error === "reconnect_required";
  };

  useEffect(() => {
    if (q.data) setForm({
      api_id: q.data.api_id || "",
      api_hash: "",
      phone: q.data.phone || "",
      alerts_channel: q.data.alerts_channel || "",
    });
    if (q.data?.reconnect_required) setReconnectRequired(true);
  }, [q.data]);

  const save = useMutation({
    mutationFn: () => settings.putTelegram({
      api_id: form.api_id ? Number(form.api_id) : null,
      api_hash: form.api_hash || null,
      phone: form.phone || null,
      channel: null,
      alerts_channel: form.alerts_channel || null,
    }),
    onSuccess: () => { toast.success("Telegram settings saved"); qc.invalidateQueries({ queryKey: ["tg"] }); },
  });
  const connect = useMutation({
    mutationFn: () => telegram.connect(),
    onSuccess: (r) => {
      if (r.data.status === "pending_code") { setPending(true); toast.info("Code sent to Telegram"); }
      else if (r.data.status === "connected") { toast.success("Telegram connected"); qc.invalidateQueries(); }
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Connect failed"),
  });
  const verify = useMutation({
    mutationFn: () => telegram.verify(code, password),
    onSuccess: (r) => {
      if (r.data.status === "password_required") toast.info("2FA required");
      else { toast.success("Connected"); setPending(false); qc.invalidateQueries(); }
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Verify failed"),
  });
  const disconnect = useMutation({
    mutationFn: () => telegram.disconnect(),
    onSuccess: () => { toast.success("Disconnected"); qc.invalidateQueries(); },
  });
  const resetSession = useMutation({
    mutationFn: () => telegram.reset(),
    onSuccess: (r) => {
      toast.success(`Session reset (removed ${r.data.removed_files} file${r.data.removed_files !== 1 ? "s" : ""}). Enter code + phone again.`);
      setReconnectRequired(false);
      qc.invalidateQueries();
    },
    onError: (e) => toast.error("Reset failed: " + extractError(e, "unknown error")),
  });
  const [healthResult, setHealthResult] = useState(null);
  const healthCheck = useMutation({
    mutationFn: () => telegram.healthCheck(),
    onSuccess: (r) => setHealthResult(r.data),
    onError: (e) => toast.error("Health check failed: " + extractError(e, "unknown error")),
  });
  const reattach = useMutation({
    mutationFn: () => telegram.reattach(),
    onSuccess: (r) => {
      toast.success(`Listener re-attached to: ${r.data.channel || "ALL chats"}`);
      setReconnectRequired(false);
      qc.invalidateQueries();
    },
    onError: (e) => {
      if (isReconnectRequired(e)) {
        setReconnectRequired(true);
        toast.error("Telegram session revoked — please reconnect");
      } else {
        toast.error(extractError(e, "Re-attach failed"));
      }
    },
  });
  const testAlert = useMutation({
    mutationFn: () => telegram.testAlert(),
    onSuccess: (r) => {
      if (r.data.ok) toast.success("Test alert sent — check your Telegram!");
      else toast.error(r.data.message || "Alert not sent");
    },
    onError: (e) => toast.error(extractError(e, "Test alert failed")),
  });

  const runtimeConnected = q.data?.runtime_status === "connected";
  const sessionExists = runtimeConnected || q.data?.status === "connected";

  return (
    <div className="card-surface p-6" data-testid="section-telegram">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <TelegramLogo size={22} color="#0891B2" weight="fill" />
          <h3 className="font-display text-xl">Telegram</h3>
        </div>
        <StatusPill
          status={q.isLoading ? "loading" :
                  q.data?.runtime_status === "connected" ? "connected" :
                  q.data?.status === "pending_code" ? "pending_code" : "error"}
          label={q.isLoading ? "checking…" : (q.data?.runtime_status || q.data?.status || "disconnected")}
        />
      </div>

      {reconnectRequired && (
        <div className="mb-4 rounded-md border border-red-300 bg-red-50 p-4 space-y-3"
             data-testid="tg-reconnect-banner" role="alert">
          <div className="flex items-start gap-3">
            <div className="text-red-600 text-xl leading-none mt-0.5">⚠</div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-red-900">Telegram session revoked</p>
              <p className="text-sm text-red-800 mt-1">
                Your login has been invalidated by Telegram — usually because the session was
                terminated from another device, or the same session key was seen from two IPs
                (preview vs production). The dead session has been auto-purged. Click
                <strong> Reconnect </strong> to log back in with a fresh code.
              </p>
            </div>
          </div>
          <div className="flex gap-2 pl-8">
            <Button data-testid="tg-reconnect-now-btn" size="sm"
                    onClick={() => { setReconnectRequired(false); connect.mutate(); }}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    disabled={connect.isPending}>
              <PlugsConnected size={16} className="mr-2" /> Reconnect now
            </Button>
            <Button data-testid="tg-reconnect-dismiss-btn" size="sm" variant="ghost"
                    onClick={() => setReconnectRequired(false)}>
              Dismiss
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        <Field label="API ID" value={form.api_id} onChange={(v) => setForm({ ...form, api_id: v })}
               testId="tg-api-id" type="number" placeholder="123456" />
        <Field label={`API Hash ${q.data?.api_hash_masked ? "(saved: " + q.data.api_hash_masked + ")" : ""}`}
               value={form.api_hash} onChange={(v) => setForm({ ...form, api_hash: v })}
               testId="tg-api-hash" placeholder="Enter to update…" type="password" />
        <Field label="Phone (E.164)" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })}
               testId="tg-phone" placeholder="+15551234567" />
        <Field label="Alerts channel (optional — where the bot posts system alerts)"
               value={form.alerts_channel}
               onChange={(v) => setForm({ ...form, alerts_channel: v })}
               testId="tg-alerts-channel"
               placeholder="Leave empty to use Saved Messages (no push)" />
      </div>

      <p className="mt-3 text-[11px] text-slate-500">
        Per-signal-channel routing lives on each Channel row (Channels tab). This card only holds the account-level Telethon session.
      </p>

      <div className="flex gap-2 mt-4 flex-wrap">
        <Button data-testid="tg-save-btn" onClick={() => save.mutate()} variant="outline"
                className="border-slate-200 hover:bg-slate-100">Save</Button>
        <Button data-testid="tg-connect-btn" onClick={() => connect.mutate()}
                className="bg-[#4F46E5] hover:bg-[#4338CA]" disabled={connect.isPending}>
          <PlugsConnected size={16} className="mr-2" /> Connect
        </Button>
        <Button data-testid="tg-reattach-btn" onClick={() => reattach.mutate()} variant="outline"
                className="border-amber-300 text-amber-600 hover:bg-amber-50"
                disabled={!sessionExists}>
          <ArrowsClockwise size={16} className="mr-2" /> Re-attach
        </Button>
        <Button data-testid="tg-health-btn" onClick={() => healthCheck.mutate()} variant="outline"
                size="sm" className="border-slate-300 hover:bg-slate-50"
                disabled={!sessionExists || healthCheck.isPending}>
          {healthCheck.isPending ? "Checking..." : "🔬 Live check"}
        </Button>
        <Button data-testid="tg-test-alert-btn" onClick={() => testAlert.mutate()} variant="outline"
                size="sm" className="border-slate-300 hover:bg-slate-50"
                disabled={!sessionExists || testAlert.isPending}>
          {testAlert.isPending ? "Sending..." : "🔔 Send test alert"}
        </Button>
        <Button data-testid="tg-disconnect-btn" onClick={() => disconnect.mutate()} variant="ghost"
                className="text-red-600 hover:bg-red-50">Disconnect</Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button data-testid="tg-reset-btn" variant="ghost"
                    className="text-red-700 hover:bg-red-50 text-xs">
              🧹 Reset session
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="tg-reset-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-red-700">Delete Telegram session file?</AlertDialogTitle>
              <AlertDialogDescription>
                Use this when Telegram invalidated the session (e.g. <em>&quot;auth key was used
                under two different IP addresses&quot;</em>). This happens after a redeploy where
                the same session file leaked between preview and production.
                <div className="mt-3 p-3 rounded bg-slate-50 border border-slate-200 text-slate-700 text-xs space-y-1">
                  <p>This will:</p>
                  <ul className="list-disc pl-5">
                    <li>Disconnect the current listener</li>
                    <li>Delete the SQLite session file from the server</li>
                    <li>Clear all in-memory Telegram state</li>
                  </ul>
                  <p className="mt-2">You&apos;ll need to <strong>Connect + Verify</strong> again with a fresh code from Telegram.</p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="tg-reset-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="tg-reset-confirm"
                onClick={(e) => { e.preventDefault(); resetSession.mutate(); }}
                disabled={resetSession.isPending}
                className="bg-red-600 hover:bg-red-700">
                {resetSession.isPending ? "Resetting..." : "Yes, reset session"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {healthResult && (
        <div className="mt-4 p-3 rounded-md border text-xs space-y-1.5"
             style={{
               background: healthResult.verdict === "healthy" ? "#F0FDF4" : "#FEF3C7",
               borderColor: healthResult.verdict === "healthy" ? "#86EFAC" : "#FCD34D",
             }}
             data-testid="tg-health-panel">
          <div className="flex items-center justify-between">
            <p className="font-semibold text-slate-800">
              Live check:{" "}
              <Badge variant={healthResult.verdict === "healthy" ? "profit" : "warn"}>
                {healthResult.verdict}
              </Badge>
            </p>
            <button onClick={() => setHealthResult(null)}
                    className="text-slate-400 hover:text-slate-600 text-lg leading-none">×</button>
          </div>
          <p className="text-slate-700">{healthResult.suggestion}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mono text-[10px] mt-2">
            <div>
              <span className="text-slate-500">TCP</span>{" "}
              <span className={healthResult.tcp_connected ? "text-emerald-700" : "text-red-700"}>
                {healthResult.tcp_connected ? "✓" : "✗"}
              </span>
            </div>
            <div>
              <span className="text-slate-500">MTProto</span>{" "}
              <span className={healthResult.mtproto_ok ? "text-emerald-700" : "text-red-700"}>
                {healthResult.mtproto_ok ? "✓" : "✗"}
              </span>
            </div>
            <div>
              <span className="text-slate-500">User</span>{" "}
              <span className="text-slate-800">{healthResult.me_username || "—"}</span>
            </div>
          </div>
          {(healthResult.tcp_error || healthResult.mtproto_error) && (
            <p className="text-red-700 mono text-[10px] mt-1">
              {healthResult.tcp_error || healthResult.mtproto_error}
            </p>
          )}
        </div>
      )}

      {pending && (
        <div className="mt-4 p-4 rounded-md bg-amber-50 border border-amber-200 space-y-3">
          <p className="overline">Enter login code</p>
          <Field label="Code" value={code} onChange={setCode} testId="tg-code" placeholder="12345" />
          <Field label="2FA password (if enabled)" value={password} onChange={setPassword}
                 testId="tg-2fa" type="password" placeholder="optional" />
          <Button data-testid="tg-verify-btn" onClick={() => verify.mutate()}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white">Verify</Button>
        </div>
      )}
    </div>
  );
}

/* ============ WEEX CARD ============ */
function WeexCard() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["weex"], queryFn: async () => (await settings.getWeex()).data });
  const ecQ = useQuery({ queryKey: ["exchange", "weex"], queryFn: async () => (await exchanges.get("weex")).data });
  const [form, setForm] = useState({ api_key: "", api_secret: "", passphrase: "", base_url: "", paper_mode: true });

  useEffect(() => {
    if (q.data) setForm({
      api_key: q.data.api_key || "",
      api_secret: "",
      passphrase: "",
      base_url: q.data.base_url || "https://api-contract.weex.com",
      paper_mode: q.data.paper_mode ?? true,
    });
  }, [q.data]);

  const save = useMutation({
    mutationFn: () => settings.putWeex({
      api_key: form.api_key || null,
      api_secret: form.api_secret || null,
      passphrase: form.passphrase || null,
      base_url: form.base_url || null,
      paper_mode: form.paper_mode,
    }),
    onSuccess: () => { toast.success("WEEX settings saved"); qc.invalidateQueries({ queryKey: ["weex"] }); },
  });
  const test = useMutation({
    mutationFn: () => settings.weexTest(),
    onSuccess: (r) => toast[r.data.ok ? "success" : "error"](r.data.ok ? "WEEX reachable" : "WEEX failed"),
  });
  const toggleAuto = useMutation({
    mutationFn: (enabled) => exchanges.setAutoTrading("weex", enabled),
    onSuccess: (r) => {
      toast[r.data.auto_trading ? "success" : "info"](
        r.data.auto_trading ? "WEEX auto-trading ON" : "WEEX auto-trading OFF"
      );
      qc.invalidateQueries({ queryKey: ["exchange", "weex"] });
      qc.invalidateQueries({ queryKey: ["overview"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Toggle failed"),
  });
  const autoOn = !!ecQ.data?.auto_trading;
  const [debugData, setDebugData] = useState(null);
  const debugBal = useMutation({
    mutationFn: () => settings.weexDebugBalance(),
    onSuccess: (r) => setDebugData(r.data),
    onError: (e) => toast.error("Debug failed: " + (e.response?.data?.detail || e.message)),
  });
  const [orderDebug, setOrderDebug] = useState(null);
  const debugOrder = useMutation({
    mutationFn: () => settings.weexDebugOrder("cmt_btcusdt", "long", 5),
    onSuccess: (r) => setOrderDebug(r.data),
    onError: (e) => toast.error("Order debug failed: " + (e.response?.data?.detail || e.message)),
  });
  const copyDebug = () => {
    if (!debugData) return;
    navigator.clipboard.writeText(JSON.stringify(debugData, null, 2));
    toast.success("Copied to clipboard");
  };

  const togglePaper = useMutation({
    mutationFn: (v) => settings.putWeex({ paper_mode: v }),
    onSuccess: (_r, v) => {
      toast[v ? "info" : "warning"](
        v ? "Paper mode ON — orders simulated" : "⚠ Live mode — orders will hit WEEX",
      );
      qc.invalidateQueries({ queryKey: ["weex"] });
    },
    onError: (e) => toast.error("Toggle failed: " + (e.response?.data?.detail || e.message)),
  });

  return (
    <div className="card-surface p-6" data-testid="section-weex">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <ChartLineUp size={22} color="#059669" weight="bold" />
          <h3 className="font-display text-xl">WEEX Futures</h3>
        </div>
        <StatusPill
          status={form.paper_mode ? "paper" : q.data?.status === "connected" ? "connected" : "error"}
          label={form.paper_mode ? "PAPER" : (q.data?.status || "not configured")}
        />
      </div>

      <div className={cn(
             "flex items-center justify-between p-4 rounded-md border-2 mb-4 transition-colors",
             autoOn ? "bg-emerald-50 border-emerald-300" : "bg-red-50 border-red-300"
           )}
           data-testid="weex-auto-trading-row">
        <div className="flex items-center gap-3">
          <div className={cn("w-3 h-3 rounded-full",
                 autoOn ? "bg-emerald-500 animate-pulse" : "bg-red-500")} />
          <div>
            <p className="text-sm font-semibold">
              WEEX · {autoOn ? "ACTIVE" : "INACTIVE"}
            </p>
            <p className="text-xs text-slate-600">
              {autoOn ? "Auto-executing signals routed to WEEX." : "Signals parsed but NOT executed for WEEX."}
            </p>
          </div>
        </div>
        <Button data-testid="weex-auto-trading-switch"
                onClick={() => toggleAuto.mutate(!autoOn)}
                disabled={toggleAuto.isPending}
                size="sm"
                className={cn("min-w-[110px] font-medium",
                  autoOn ? "bg-red-600 hover:bg-red-700 text-white"
                        : "bg-emerald-600 hover:bg-emerald-700 text-white")}>
          {toggleAuto.isPending ? "…" : autoOn ? "Deactivate" : "Activate"}
        </Button>
      </div>

      <div className="flex items-center justify-between p-3 rounded-md bg-slate-50 border border-slate-200 mb-3">
        <div>
          <p className="text-sm font-medium">Paper mode {form.paper_mode ? "" : "(LIVE)"}</p>
          <p className="text-xs text-slate-500">
            {form.paper_mode ? "Orders are simulated. No calls to WEEX." : "⚠ Orders will hit WEEX with real money."}
          </p>
        </div>
        <Switch data-testid="weex-paper-switch"
                checked={form.paper_mode}
                disabled={togglePaper.isPending}
                onCheckedChange={(v) => {
                  setForm({ ...form, paper_mode: v });
                  togglePaper.mutate(v);
                }}
                className="data-[state=checked]:bg-amber-500 data-[state=unchecked]:bg-red-500" />
      </div>

      <div className="space-y-3">
        <Field label="API Key" value={form.api_key} onChange={(v) => setForm({ ...form, api_key: v })}
               testId="weex-api-key" placeholder="ACCESS-KEY" />
        <Field label="API Secret" value={form.api_secret} onChange={(v) => setForm({ ...form, api_secret: v })}
               testId="weex-api-secret" type="password" placeholder="enter to update…" />
        <Field label="Passphrase" value={form.passphrase} onChange={(v) => setForm({ ...form, passphrase: v })}
               testId="weex-passphrase" type="password" placeholder="enter to update…" />
        <Field label="Base URL" value={form.base_url} onChange={(v) => setForm({ ...form, base_url: v })}
               testId="weex-base-url" placeholder="https://api-contract.weex.com" />
      </div>

      <div className="mt-4">
        <p className="overline mb-2">Debug tools</p>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="weex-save-btn" onClick={() => save.mutate()}>Save</Button>
          <Button data-testid="weex-test-btn" onClick={() => test.mutate()}
                  variant="outline" size="sm">🔌 Test connection</Button>
          <Button data-testid="weex-debug-order-btn"
                  onClick={() => debugOrder.mutate()}
                  disabled={debugOrder.isPending}
                  variant="outline" size="sm">🔬 Dry-run probe</Button>
          <Button data-testid="weex-probe-live"
                  onClick={() => {
                    if (form.paper_mode) {
                      toast.error("Turn off paper mode first — live probe places a real $5 order.");
                      return;
                    }
                    if (confirm("This will place a REAL $5 order on WEEX. Continue?")) debugOrder.mutate();
                  }}
                  variant="outline" size="sm"
                  className="border-red-300 text-red-700 hover:bg-red-50">⚠ Live probe ($5)</Button>
          <Button data-testid="weex-fetch-balance"
                  onClick={() => debugBal.mutate()}
                  disabled={debugBal.isPending}
                  variant="outline" size="sm">💰 Fetch balance JSON</Button>
          <Button data-testid="weex-debug-btn"
                  onClick={() => debugBal.mutate()}
                  disabled={debugBal.isPending}
                  variant="outline" size="sm"
                  className="border-amber-300 text-amber-700 hover:bg-amber-50">
            🔍 Debug balance JSON
          </Button>
        </div>
      </div>

      {(debugData || orderDebug) && (
        <div className="mt-4 pt-4 border-t border-slate-200">
          {debugData && (
            <div className="mt-3 p-3 rounded-md bg-slate-50 border border-slate-200"
                 data-testid="weex-debug-panel">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-slate-700">Diagnostic result</p>
                <button onClick={copyDebug}
                        className="text-xs text-indigo-700 hover:text-indigo-900 underline"
                        data-testid="weex-debug-copy">
                  Copy JSON
                </button>
              </div>
              <div className="text-xs space-y-1 mono">
                <p>
                  <span className="text-slate-500">ping.ok:</span>{" "}
                  <Badge variant={debugData.ping?.ok ? "profit" : "loss"}>{String(debugData.ping?.ok)}</Badge>
                  {debugData.ping?.status_code && (
                    <span className="text-slate-500 ml-2">HTTP {debugData.ping.status_code}</span>
                  )}
                </p>
                <p>
                  <span className="text-slate-500">balance_found:</span>{" "}
                  <span className={debugData.balance_parse_result?.balance_found != null ? "text-emerald-700 font-semibold" : "text-red-700 font-semibold"}>
                    {debugData.balance_parse_result?.balance_found ?? "null"}
                  </span>
                </p>
                {debugData.balance_parse_result?.error && (
                  <p><span className="text-slate-500">error:</span> <span className="text-red-700">{debugData.balance_parse_result.error}</span></p>
                )}
              </div>
              <p className="text-[10px] text-slate-500 mt-2">{debugData.hint}</p>
              <p className="text-[10px] font-semibold text-slate-600 mt-3 mb-1">Raw response (paste this to widen the parser):</p>
              <pre className="text-[10px] mono bg-white border border-slate-200 rounded p-2 max-h-64 overflow-auto">
{JSON.stringify({v2: debugData.raw_v2_assets_response, v3: debugData.raw_v3_assets_response}, null, 2)}
              </pre>
            </div>
          )}

          {orderDebug && (
            <div className="mt-3 p-3 rounded-md bg-slate-50 border border-red-200"
                 data-testid="weex-order-debug-panel">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-slate-700">Order-shape probe</p>
                <button onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify(orderDebug, null, 2));
                  toast.success("Copied to clipboard");
                }}
                        className="text-xs text-indigo-700 hover:text-indigo-900 underline"
                        data-testid="weex-order-debug-copy">
                  Copy JSON
                </button>
              </div>
              <p className="text-[10px] text-slate-500 mb-2">
                Symbol: <span className="mono">{orderDebug.symbol}</span> · Side:{" "}
                <span className="mono">{orderDebug.side}</span> · Qty: <span className="mono">{orderDebug.computed_qty}</span> · Mark: <span className="mono">{orderDebug.mark_price ?? "n/a"}</span>
              </p>
              <p className="text-[10px] text-slate-500 mb-2">{orderDebug.hint}</p>
              <pre className="text-[10px] mono bg-white border border-slate-200 rounded p-2 max-h-96 overflow-auto">
{JSON.stringify(orderDebug.attempts, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ============ RISK CARD (single-channel / legacy view) ============ */
const RISK_FIELD_LABELS = {
  auto_trading: "Auto-trading",
  risk_percent_per_trade: "% per trade",
  max_leverage: "Max leverage",
  default_leverage: "Default leverage",
  follow_provider_leverage: "Follow provider leverage",
  execute_without_provider_sl: "Execute without provider SL",
  internal_fallback_sl_percent: "Fallback SL %",
  allow_provider_sl_replace: "Allow SL replace",
  allow_wider_provider_sl: "Allow wider SL",
  close_full_at_tp1: "Close 100% at TP1",
  require_manual_approval_ai_signals: "Manual AI approval",
  auto_close_no_sl_minutes: "Auto-close no-SL",
  hard_leverage_ceiling: "Hard leverage ceiling",
  max_concurrent_positions: "Max concurrent positions",
  max_notional_per_trade_usdt: "Per-trade notional cap",
  daily_loss_pct_kill_switch: "Daily loss kill",
  symbol_cooldown_seconds: "Symbol cooldown",
  dedupe_window_seconds: "Dedupe window",
  place_exchange_sl_order: "Exchange-side SL",
  max_entry_slippage_pct: "Entry slippage cap",
  telegram_alerts_enabled: "Telegram alerts",
  sl_to_breakeven_at_roi_pct: "BE trigger",
  sl_to_breakeven_offset_pct: "BE offset",
  auto_close_roi_pct: "Auto-close ROI",
};

function getDirtyLabels(current, baseline) {
  const out = [];
  for (const key of Object.keys(RISK_FIELD_LABELS)) {
    const a = current?.[key];
    const b = baseline?.[key];
    const ea = a === undefined || a === null ? "" : String(a);
    const eb = b === undefined || b === null ? "" : String(b);
    if (ea !== eb) out.push(RISK_FIELD_LABELS[key]);
  }
  return out;
}

function RiskCard() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["risk"], queryFn: async () => (await settings.getRisk()).data });
  const [f, setF] = useState({});
  const [baseline, setBaseline] = useState({});

  useEffect(() => {
    if (q.data && Object.keys(baseline).length === 0) {
      setF(q.data);
      setBaseline(q.data);
    }
  }, [q.data, baseline]);

  const save = useMutation({
    mutationFn: () => settings.putRisk({
      auto_trading: f.auto_trading,
      risk_percent_per_trade: Number(f.risk_percent_per_trade),
      max_leverage: Number(f.max_leverage),
      default_leverage: Number(f.default_leverage),
      follow_provider_leverage: f.follow_provider_leverage,
      execute_without_provider_sl: f.execute_without_provider_sl,
      internal_fallback_sl_percent: Number(f.internal_fallback_sl_percent),
      allow_provider_sl_replace: f.allow_provider_sl_replace,
      allow_wider_provider_sl: f.allow_wider_provider_sl,
      close_full_at_tp1: f.close_full_at_tp1,
      require_manual_approval_ai_signals: f.require_manual_approval_ai_signals,
      auto_close_no_sl_minutes: Number(f.auto_close_no_sl_minutes || 0),
      hard_leverage_ceiling: Number(f.hard_leverage_ceiling || 25),
      max_concurrent_positions: Number(f.max_concurrent_positions || 0),
      max_notional_per_trade_usdt: Number(f.max_notional_per_trade_usdt || 0),
      daily_loss_pct_kill_switch: Number(f.daily_loss_pct_kill_switch || 0),
      symbol_cooldown_seconds: Number(f.symbol_cooldown_seconds || 0),
      dedupe_window_seconds: Number(f.dedupe_window_seconds ?? 600),
      place_exchange_sl_order: f.place_exchange_sl_order,
      max_entry_slippage_pct: Number(f.max_entry_slippage_pct ?? 0.3),
      telegram_alerts_enabled: f.telegram_alerts_enabled,
      sl_to_breakeven_at_roi_pct: Number(f.sl_to_breakeven_at_roi_pct ?? 0),
      sl_to_breakeven_offset_pct: Number(f.sl_to_breakeven_offset_pct ?? 0),
      auto_close_roi_pct: Number(f.auto_close_roi_pct ?? 0),
    }),
    onSuccess: () => {
      toast.success("Risk settings saved");
      setBaseline({ ...f });
      qc.invalidateQueries();
    },
  });
  const discard = () => { setF({ ...baseline }); toast.info("Changes discarded"); };
  const upd = (k) => (v) => setF({ ...f, [k]: v });

  const dirtyLabels = getDirtyLabels(f, baseline);
  const isDirty = dirtyLabels.length > 0;

  return (
    <div className="card-surface p-6" data-testid="section-risk">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <ShieldCheck size={22} color="#D97706" weight="fill" />
          <h3 className="font-display text-xl">Risk & Guardrails</h3>
        </div>
        {isDirty && <Badge variant="warn" data-testid="risk-dirty-badge">Unsaved</Badge>}
      </div>

      {isDirty && (
        <div className="mb-4 p-3 rounded-md border border-amber-300 bg-amber-50 flex items-start gap-3"
             data-testid="risk-unsaved-warning">
          <div className="text-amber-600 text-lg leading-none mt-0.5">⚠</div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-amber-800 uppercase tracking-wider">Unsaved changes</p>
            <p className="text-xs text-amber-900 mt-1">
              {dirtyLabels.slice(0, 4).join(" · ")}
              {dirtyLabels.length > 4 ? ` · +${dirtyLabels.length - 4} more` : ""}
            </p>
          </div>
          <button data-testid="risk-discard-btn" onClick={discard}
                  className="text-xs text-amber-700 hover:text-amber-900 underline shrink-0">
            Discard
          </button>
        </div>
      )}

      <div className="space-y-4">
        <ToggleRow label="Auto-trading" desc="Execute parsed signals automatically"
                   checked={!!f.auto_trading} onChange={upd("auto_trading")} testId="risk-auto-trading" />
        <ToggleRow label="Follow provider leverage" desc="Use signal's leverage when specified (still capped by max)"
                   checked={!!f.follow_provider_leverage} onChange={upd("follow_provider_leverage")} testId="risk-follow-lev" />
        <ToggleRow label="Execute without provider SL" desc="Attach internal fallback SL if missing"
                   checked={!!f.execute_without_provider_sl} onChange={upd("execute_without_provider_sl")} testId="risk-exec-no-sl" />
        <ToggleRow label="Allow provider SL to replace internal" desc="Move to provider SL when it arrives"
                   checked={!!f.allow_provider_sl_replace} onChange={upd("allow_provider_sl_replace")} testId="risk-replace" />
        <ToggleRow label="Allow wider provider SL" desc="Off by default — dangerous"
                   checked={!!f.allow_wider_provider_sl} onChange={upd("allow_wider_provider_sl")} testId="risk-wider" />
        <ToggleRow label="Close 100% at TP1" desc="Book the full trade on first TP hit (vs 33% partial)"
                   checked={!!f.close_full_at_tp1} onChange={upd("close_full_at_tp1")} testId="risk-close-tp1" />
        <ToggleRow label="Require manual approval for AI signals"
                   desc="AI-parsed signals wait in a queue for human approve/reject (regex-parsed signals still auto-execute)"
                   checked={!!f.require_manual_approval_ai_signals}
                   onChange={upd("require_manual_approval_ai_signals")}
                   testId="risk-manual-approval-ai" />

        <SliderRow label={`% of balance per trade — ${f.risk_percent_per_trade ?? "-"}%`}
                   value={[f.risk_percent_per_trade ?? 1]} onValueChange={(v) => setF({ ...f, risk_percent_per_trade: v[0] })}
                   min={0.1} max={20} step={0.1} testId="risk-percent" />
        <SliderRow label={`Default leverage (when signal has none) — ${f.default_leverage ?? "-"}x`}
                   value={[f.default_leverage ?? 3]} onValueChange={(v) => setF({ ...f, default_leverage: v[0] })}
                   min={1} max={50} step={1} testId="risk-default-lev" />
        <SliderRow label={`Max leverage (hard cap) — ${f.max_leverage ?? "-"}x`}
                   value={[f.max_leverage ?? 10]} onValueChange={(v) => setF({ ...f, max_leverage: v[0] })}
                   min={1} max={125} step={1} testId="risk-leverage" />
        <SliderRow label={`Internal fallback SL — ${f.internal_fallback_sl_percent ?? "-"}%`}
                   value={[f.internal_fallback_sl_percent ?? 2]} onValueChange={(v) => setF({ ...f, internal_fallback_sl_percent: v[0] })}
                   min={0.1} max={20} step={0.1} testId="risk-fallback-sl" />
      </div>

      <div className="mt-6 pt-5 border-t border-slate-200 space-y-4" data-testid="section-circuit-breakers">
        <div>
          <p className="overline text-red-700">🛡 Go-live safety</p>
          <h4 className="font-display text-lg mt-1">Circuit breakers</h4>
          <p className="text-xs text-slate-500">
            Hard limits that protect your account from cascading losses & runaway orders. Recommended defaults are shown; use $0 = unlimited.
          </p>
        </div>

        <SliderRow label={`Hard leverage ceiling (absolute cap) — ${f.hard_leverage_ceiling ?? "-"}x`}
                   value={[f.hard_leverage_ceiling ?? 25]}
                   onValueChange={(v) => setF({ ...f, hard_leverage_ceiling: v[0] })}
                   min={1} max={125} step={1} testId="risk-hard-lev-ceiling" />
        <SliderRow label={`Max concurrent open positions — ${f.max_concurrent_positions || "unlimited"}`}
                   value={[f.max_concurrent_positions ?? 5]}
                   onValueChange={(v) => setF({ ...f, max_concurrent_positions: v[0] })}
                   min={0} max={20} step={1} testId="risk-max-concurrent" />
        <SliderRow label={`Per-trade notional cap (USDT) — ${f.max_notional_per_trade_usdt ? `$${f.max_notional_per_trade_usdt}` : "unlimited"} · use for canary/first-live runs`}
                   value={[f.max_notional_per_trade_usdt ?? 0]}
                   onValueChange={(v) => setF({ ...f, max_notional_per_trade_usdt: v[0] })}
                   min={0} max={1000} step={5} testId="risk-notional-cap" />
        <SliderRow label={`Daily loss kill-switch — ${f.daily_loss_pct_kill_switch ? `-${f.daily_loss_pct_kill_switch}%` : "disabled"}`}
                   value={[f.daily_loss_pct_kill_switch ?? 0]}
                   onValueChange={(v) => setF({ ...f, daily_loss_pct_kill_switch: v[0] })}
                   min={0} max={20} step={0.5} testId="risk-daily-kill" />
        <SliderRow label={`Same-symbol cooldown — ${f.symbol_cooldown_seconds ? `${f.symbol_cooldown_seconds}s` : "off"}`}
                   value={[f.symbol_cooldown_seconds ?? 0]}
                   onValueChange={(v) => setF({ ...f, symbol_cooldown_seconds: v[0] })}
                   min={0} max={3600} step={30} testId="risk-symbol-cooldown" />
        <SliderRow label={`Duplicate-message window — ${f.dedupe_window_seconds ? `${f.dedupe_window_seconds}s` : "off"}`}
                   value={[f.dedupe_window_seconds ?? 600]}
                   onValueChange={(v) => setF({ ...f, dedupe_window_seconds: v[0] })}
                   min={0} max={3600} step={30} testId="risk-dedupe-window" />
        <SliderRow label={`Max entry slippage — ${f.max_entry_slippage_pct ? `${f.max_entry_slippage_pct}%` : "off (any drift accepted)"} · reject trade if market drifted more than this in the adverse direction`}
                   value={[f.max_entry_slippage_pct ?? 0.3]}
                   onValueChange={(v) => setF({ ...f, max_entry_slippage_pct: v[0] })}
                   min={0} max={2} step={0.05} testId="risk-max-slippage" />

        {/* ---- Move-SL-to-Breakeven trigger ---- */}
        <div className="pt-2 mt-2 border-t border-dashed border-slate-200">
          <p className="overline text-emerald-700 mb-2">🛡 Move SL to breakeven at ROI</p>
        </div>
        <SliderRow label={`BE trigger — ${f.sl_to_breakeven_at_roi_pct ? `move SL to entry when unrealized ROI ≥ ${f.sl_to_breakeven_at_roi_pct}%` : "off (never auto-move SL)"} · fires once per trade`}
                   value={[f.sl_to_breakeven_at_roi_pct ?? 0]}
                   onValueChange={(v) => setF({ ...f, sl_to_breakeven_at_roi_pct: v[0] })}
                   min={0} max={500} step={5} testId="risk-be-roi-threshold" />
        <SliderRow label={`BE offset — ${f.sl_to_breakeven_offset_pct ? `${f.sl_to_breakeven_offset_pct}% ${f.sl_to_breakeven_offset_pct > 0 ? "past entry (covers round-trip fees)" : "before entry (locks small loss)"}` : "0% (exact entry price)"}`}
                   value={[f.sl_to_breakeven_offset_pct ?? 0]}
                   onValueChange={(v) => setF({ ...f, sl_to_breakeven_offset_pct: v[0] })}
                   min={-0.5} max={2} step={0.05} testId="risk-be-offset" />

        {/* ---- Full auto-close at ROI ---- */}
        <SliderRow label={`Auto-close full position — ${f.auto_close_roi_pct ? `close 100% when ROI ≥ ${f.auto_close_roi_pct}%` : "off (rely on provider TPs)"} · a hard take-profit safety net`}
                   value={[f.auto_close_roi_pct ?? 0]}
                   onValueChange={(v) => setF({ ...f, auto_close_roi_pct: v[0] })}
                   min={0} max={500} step={10} testId="risk-auto-close-roi" />

        <ToggleRow label="Place exchange-side stop order (live mode)"
                   desc="Puts a real conditional SL on WEEX so protection survives app crashes"
                   checked={f.place_exchange_sl_order !== false}
                   onChange={upd("place_exchange_sl_order")}
                   testId="risk-place-exch-sl" />
        <ToggleRow label="Telegram alerts"
                   desc="DM your Saved Messages when kill switch fires, Telegram disconnects, or a trade errors"
                   checked={f.telegram_alerts_enabled !== false}
                   onChange={upd("telegram_alerts_enabled")}
                   testId="risk-telegram-alerts" />
      </div>

      <Button data-testid="risk-save-btn" onClick={() => save.mutate()}
              disabled={!isDirty || save.isPending}
              className={cn(
                "w-full mt-4 text-white transition-all",
                isDirty
                  ? "bg-amber-500 hover:bg-amber-600 shadow-md ring-2 ring-amber-200 animate-pulse-slow"
                  : "bg-slate-300 hover:bg-slate-300 cursor-not-allowed",
              )}>
        {isDirty ? `Save ${dirtyLabels.length} change${dirtyLabels.length > 1 ? "s" : ""}` : "No changes"}
      </Button>
    </div>
  );
}
